// CDC refetch coalescing for the table models.
//
// Both table models used to answer every CDC event with its own refetch of that row. Every
// DSL view subscribes to CDC on its backing table by default, so one open tab issued one
// dbaccess query per change on every table it showed: measured on continuum-prod 2026-09-12,
// one tab sent 28,000 single-row queries in four hours — ~80 a minute, 200+ at each
// five-minute schedule burst, exactly the processrun + circuitbreaker_event + jobevent churn.
// BUG-20260913-cdc-refetch-per-event-hammers-dbaccess.
//
// A THROTTLE, NOT A DEBOUNCE. The first event opens a window; every row id that arrives
// inside it is collected; one flush runs when the window closes. A trailing debounce
// (restart the timer on every event) never fires while events keep arriving, and processrun
// changes every few seconds all day — the view would go stale exactly when it is busiest.
// The throttle bounds both sides: at most one flush per window, and no change waits longer
// than one window to appear.

/** How long CDC-triggered row refetches are collected before one refetch runs (agreed with glen). */
export const CDC_REFETCH_WINDOW_MS = 5_000;

/** Largest `uid IN (...)` list a single refetch sends; a burst larger than this is split. */
export const CDC_REFETCH_MAX_IDS = 200;

export interface CdcRefetchCoalescer {
  /** Collect row ids to refetch; opens a window if none is open. */
  add(rowIds: Iterable<string>): void;
  /** Drop pending ids and any open window (view teardown). */
  cancel(): void;
}

/**
 * flush receives the window's distinct row ids, in arrival order, at most CDC_REFETCH_MAX_IDS
 * per call. A rejected flush is logged and dropped: the next change, the interval refresh or
 * a manual refresh brings the row back, and retrying here would re-create the storm.
 */
export function createCdcRefetchCoalescer(
  flush: (rowIds: string[]) => void | Promise<void>,
  windowMs: number = CDC_REFETCH_WINDOW_MS,
  maxIds: number = CDC_REFETCH_MAX_IDS,
): CdcRefetchCoalescer {
  let pending = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const fire = () => {
    timer = undefined;
    const ids = Array.from(pending);
    pending = new Set();
    for (let i = 0; i < ids.length; i += maxIds) {
      try {
        // Promise.resolve rather than instanceof: a promise from another realm (jsdom, a
        // polyfill) is still a thenable and must not escape as an unhandled rejection.
        Promise.resolve(flush(ids.slice(i, i + maxIds))).catch((e) =>
          console.error("CDC refetch failed:", e),
        );
      } catch (e) {
        console.error("CDC refetch failed:", e);
      }
    }
  };

  return {
    add(rowIds) {
      for (const id of rowIds) pending.add(id);
      if (pending.size > 0 && timer === undefined) {
        timer = setTimeout(fire, windowMs);
      }
    },
    cancel() {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      pending = new Set();
    },
  };
}
